import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { resolveFileDetailTab } from "lib/utils/fileDetailTabs";
import { getFileList } from "lib/api/getfileList";
import { getFileSortPref } from "lib/utils/fileSortpref";
import { useAppStore } from "lib/store/useAppStore";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "css/fileDetailNavigation.css";

export default function PrevNextNavToFIleIndex({
  prevFile,
  nextFile,
  project_id,
  upload_id,
  currentTab,
}) {
  const { search } = useLocation();
  const userId = useAppStore(state => state.user?.id);
  const params = new URLSearchParams(search);
  const filterKeys = ["category", "index", "value", "treeBuildingName", "treeFloor", "treeLocation", "treeFacilityType", "treeFacilityName", "treePartName", "treeNodeId", "fileType", "facilityType", "fusionResult", "searchCategory", "searchKeyword"];
  const scopeSearch = new URLSearchParams(filterKeys.filter(key => params.has(key)).map(key => [key, params.get(key)])).toString();
  const scoped = Boolean(scopeSearch);
  const page = Math.max(1, Number.parseInt(params.get("page"), 10) || 1);
  const limit = Math.max(1, Number.parseInt(params.get("limit"), 10) || 20);
  const requestKey = `${project_id}:${upload_id}:${scopeSearch}:${page}:${limit}`;
  const [scopedFiles, setScopedFiles] = useState(null);

  useEffect(() => {
    if (!scoped || !project_id || !upload_id || !userId) return;
    let active = true;
    setScopedFiles(null);
    const filters = Object.fromEntries(new URLSearchParams(scopeSearch));
    const sort = getFileSortPref(userId);
    const options = { ...filters, ...sort, limit };
    const load = async () => {
      const current = await getFileList(project_id, { ...options, page });
      if (!active || !current.success) return;
      const index = current.files.findIndex(file => String(file.id) === String(upload_id));
      if (index < 0) return;
      let previous = index > 0 ? { file: current.files[index - 1], page } : null;
      let next = index < current.files.length - 1 ? { file: current.files[index + 1], page } : null;
      const requests = [];
      if (!previous && page > 1) requests.push(getFileList(project_id, { ...options, page: page - 1 }).then(result => {
        if (result.success && result.files.length) previous = { file: result.files[result.files.length - 1], page: page - 1 };
      }));
      if (!next && page < (current.pagination?.totalPages ?? 1)) requests.push(getFileList(project_id, { ...options, page: page + 1 }).then(result => {
        if (result.success && result.files.length) next = { file: result.files[0], page: page + 1 };
      }));
      await Promise.all(requests);
      if (active) setScopedFiles({ requestKey, previous, next });
    };
    load();
    return () => { active = false; };
  }, [scoped, scopeSearch, project_id, upload_id, userId, page, limit, requestKey]);

  const currentScopedFiles = scopedFiles?.requestKey === requestKey ? scopedFiles : null;
  const previous = scoped ? currentScopedFiles?.previous : prevFile && { file: prevFile, page };
  const next = scoped ? currentScopedFiles?.next : nextFile && { file: nextFile, page };

  const getFileLink = ({ file, page: targetPage }) => {
    const fileType = file?.file_type === "image" ? "image" : "video";
    const nextSearch = new URLSearchParams(search);
    if (scoped) nextSearch.set("page", String(targetPage));
    nextSearch.set("tab", resolveFileDetailTab(currentTab ?? nextSearch.get("tab"), fileType));
    return {
      pathname: `/project/${project_id}/file_${fileType}/${file?.id ?? ""}`,
      search: `?${nextSearch.toString()}`,
    };
  };

  return (
    <ul className="fileIndexNavigation" aria-label="이전·다음 파일 이동">
      {previous && (
        <li className="fileIndexNavigation__previous">
          <Link
            replace
            to={getFileLink(previous)}
            aria-label={`이전 파일: ${previous.file?.file_name || ""}`}
          >
            <ArrowLeft size={16} aria-hidden="true" /><span><strong>{previous.file?.file_name}</strong></span>
          </Link>
        </li>
      )}
      {next && (
        <li className="fileIndexNavigation__next">
          <Link
            replace
            to={getFileLink(next)}
            aria-label={`다음 파일: ${next.file?.file_name || ""}`}
          >
            <span><strong>{next.file?.file_name}</strong></span><ArrowRight size={16} aria-hidden="true" />
          </Link>
        </li>
      )}
    </ul>
  );
}
